import React, { ChangeEvent, Fragment } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { always } from 'ramda';

import { renderWhenTrue, renderWhenTrueOtherwise } from '@utils/rendering';
import { SpaceViewStep } from '@hooks/useArtworkSpaceView/useArtworkSpaceView';
import { Button } from '@components/form';
import { SpaceViewButton } from '@components/PDP/spaceViewButton';
import {
  CheckedIcon,
  FieldContainer,
  FieldLabel,
  UploadInputField,
  StepTitle,
  DownloadButtonWrapper,
  InputFieldComponent,
} from '@components/PDP/pdp.styles';
import { KLARNA_CONTAINER_OFFSET } from '@components/klarnaPlacement';
import { commonMessages } from '@components/common';

import {
  CloseButton,
  ConfirmButtonWrapper,
  Container,
  Header,
  Wrapper,
  Gradient,
  SpaceViewButtonWrapper,
  Content,
} from './artworkSpaceViewDetails.styles';

export interface ArtworkSpaceViewDetailsProps {
  isSpaceViewMode: boolean;
  descriptionHeight: number;
  toggleSpaceViewMode: () => void;
  openSpaceViewMode: () => void;
  setBackgroundImage: (value: string) => void;
  backgroundImage: string;
  measure: number;
  distance: number;
  setMeasure: (value: number) => void;
  spaceViewStep: SpaceViewStep;
  setSpaceViewStep: (value: number) => void;
  downloadImage: () => void;
}

export const ArtworkSpaceViewDetails = ({
  isSpaceViewMode,
  toggleSpaceViewMode,
  openSpaceViewMode,
  setBackgroundImage,
  backgroundImage,
  measure,
  setMeasure,
  spaceViewStep,
  setSpaceViewStep,
  downloadImage,
  distance,
  descriptionHeight,
}: ArtworkSpaceViewDetailsProps) => {
  const intl = useIntl();
  const isBackgroundImage = !!backgroundImage.length;

  const handleConfirm = () => setSpaceViewStep(SpaceViewStep.Third);

  const handleChangeValue = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value || '0';
    setMeasure(parseInt(value, 10));
  };

  const renderCheckedIcon = renderWhenTrue(always(<CheckedIcon />));
  const renderMeasure = renderWhenTrue(
    always(
      <InputFieldComponent
        name="measure"
        label="Length:"
        additionalInformation="(inches)"
        min="0"
        type="number"
        pattern="[0-9]*"
        onChange={handleChangeValue}
        value={`${measure}`}
        disabled={spaceViewStep !== SpaceViewStep.Second}
      />
    )
  );
  const renderConfirmButton = renderWhenTrue(
    always(
      <ConfirmButtonWrapper>
        <Button onClick={handleConfirm} disabled={!distance || !measure} fullWidth>
          <FormattedMessage id="artworkSpaceViewDetails.confirm" defaultMessage="CONFIRM" />
        </Button>
      </ConfirmButtonWrapper>
    )
  );
  const renderMeasureDescription = renderWhenTrue(
    always(
      <FieldLabel>
        <FormattedMessage
          id="artworkSpaceViewDetails.measureDescription"
          defaultMessage="Click any two points in the photo, and let us know the distance, in real life, between them. The points could refer to the height of a vase or to a length of the wall."
        />
      </FieldLabel>
    )
  );
  const renderDownload = renderWhenTrue(
    always(
      <Fragment>
        <FieldLabel>
          <FormattedMessage
            id="artworkSpaceViewDetails.positionDescription"
            defaultMessage="Place the artwork thumbnail where you would like to see it in your room. Click to set the thumbnail in place."
          />
        </FieldLabel>
        <DownloadButtonWrapper>
          <Button onClick={downloadImage} inverted disabled={spaceViewStep !== SpaceViewStep.Third} fullWidth>
            <FormattedMessage id="artworkSpaceViewDetails.download" defaultMessage="Download Image" />
          </Button>
        </DownloadButtonWrapper>
      </Fragment>
    )
  );

  const renderFirstStep = () => {
    return (
      <FieldContainer>
        <StepTitle onClick={() => setSpaceViewStep(SpaceViewStep.First)}>
          <FormattedMessage id="artworkSpaceViewDetails.upload" defaultMessage="① Upload:" />
          {renderCheckedIcon(!!backgroundImage)}
        </StepTitle>
        {spaceViewStep === SpaceViewStep.First && (
          <FieldLabel>
            <FormattedMessage
              id="artworkSpaceViewDetails.uploadDescription"
              defaultMessage="Upload a photo of your room, taken with the camera directly facing the wall"
            />
          </FieldLabel>
        )}
        <UploadInputField
          name="background"
          accept="image/*"
          handleChange={setBackgroundImage}
          disabled={spaceViewStep !== SpaceViewStep.First}
        >
          <FormattedMessage id="artworkSpaceViewDetails.selectImage" defaultMessage="SELECT IMAGE" />
        </UploadInputField>
      </FieldContainer>
    );
  };

  const renderSecondStep = () => (
    <FieldContainer disabled={spaceViewStep === SpaceViewStep.First && !isBackgroundImage}>
      <StepTitle
        disabled={spaceViewStep === SpaceViewStep.First && !isBackgroundImage}
        onClick={() => setSpaceViewStep(SpaceViewStep.Second)}
      >
        <FormattedMessage id="artworkSpaceViewDetails.measure" defaultMessage="② Measure:" />
        {renderCheckedIcon(!!distance && !!measure)}
      </StepTitle>
      {renderMeasureDescription(spaceViewStep === SpaceViewStep.Second)}
      {renderMeasure(spaceViewStep === SpaceViewStep.Second || (!!distance && !!measure))}
      {renderConfirmButton(spaceViewStep === SpaceViewStep.Second)}
    </FieldContainer>
  );

  const renderThirdStep = () => {
    const isRestStepsComplete = distance && measure && isBackgroundImage;

    return (
      <FieldContainer disabled={spaceViewStep !== SpaceViewStep.Third && !isRestStepsComplete}>
        <StepTitle
          disabled={spaceViewStep !== SpaceViewStep.Third && !isRestStepsComplete}
          onClick={() => setSpaceViewStep(SpaceViewStep.Third)}
        >
          <FormattedMessage id="artworkSpaceViewDetails.position" defaultMessage="③ Position:" />
        </StepTitle>
        {renderDownload(spaceViewStep === SpaceViewStep.Third)}
      </FieldContainer>
    );
  };

  const renderContent = () => (
    <Wrapper>
      <Gradient />
      <Content>
        <Header>
          <StepTitle>
            <FormattedMessage id="artworkSpaceViewDetails.viewSpace" defaultMessage="View in your space:" />
          </StepTitle>
          <CloseButton onClick={toggleSpaceViewMode} aria-label={intl.formatMessage(commonMessages.closeLabel)} />
        </Header>
        {renderFirstStep()}
        {renderSecondStep()}
        {renderThirdStep()}
      </Content>
    </Wrapper>
  );

  return (
    <Container>
      {renderWhenTrueOtherwise(
        always(
          <SpaceViewButtonWrapper
            initial={{ y: descriptionHeight + 36 + KLARNA_CONTAINER_OFFSET, opacity: descriptionHeight ? 1 : 0 }}
            animate={{ y: descriptionHeight + 36 + KLARNA_CONTAINER_OFFSET, transitionEnd: { opacity: 1 } }}
            transition={{ ease: 'easeInOut', duration: 0.5 }}
          >
            {/* <SpaceViewButton handleClick={openSpaceViewMode} /> */}
          </SpaceViewButtonWrapper>
        ),
        renderContent
      )(!isSpaceViewMode)}
    </Container>
  );
};
